import React, { useState, useRef, useEffect } from 'react';

/* Custom styled dropdown — matches the Hosts filter design (uses .h-select* CSS). */
export function SelectDrop({ value, onChange, options, minWidth = 130, style }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const current = options.find(o => o.value === value) || options[0];
  return (
    <div className={`h-select${open ? ' open' : ''}`} ref={ref} style={{ minWidth, ...style }}>
      <button type="button" className="h-select-btn" onClick={() => setOpen(o => !o)}>
        <span>{current ? current.label : ''}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="h-select-menu">
          {options.map(o => (
            <button
              type="button"
              key={o.value}
              className={`h-select-opt${o.value === value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Avatar({ name = '', size = 'md', coral = false }) {
  const initials = name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const cls = `avatar${size === 'sm' ? ' avatar-sm' : ''}${coral ? ' coral' : ''}`;
  return <div className={cls}>{initials || '?'}</div>;
}

const STATUS_MAP = {
  pre_req:               { cls: 'prereq',    label: 'Pre-Req' },
  ready:                 { cls: 'ready',     label: 'Ready' },
  host_pending_response: { cls: 'outreach',  label: 'Outreach Sent' },
  interview_scheduling:  { cls: 'interview', label: 'Interviewing' },
  active:                { cls: 'active',    label: 'Active' },
  completing:            { cls: 'completing',label: 'Completing' },
  complete:              { cls: 'complete',  label: 'Complete' },
};

const AGREEMENT_MAP = {
  none:        { cls: 'no-agreement',          label: 'No Agreement',       color: '#6b7280' },
  draft:       { cls: 'agreement-draft',       label: 'Draft',              color: '#f97316' },
  sent:        { cls: 'agreement-sent',        label: 'Sent',               color: '#3b82f6' },
  viewed:      { cls: 'agreement-sent',        label: 'Viewed',             color: '#3b82f6' },
  host_signed: { cls: 'agreement-host-signed', label: 'Awaiting Dean',      color: '#8b5cf6' },
  executed:    { cls: 'agreement-executed',    label: 'Signed',             color: '#22c55e' },
  declined:    { cls: 'agreement-declined',    label: 'Declined',           color: '#ef4444' },
};

export function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || { cls: 'muted', label: status || '—' };
  return <span className={`badge ${cfg.cls}`}><span className="dot" />{cfg.label}</span>;
}

export function AgreementBadge({ status }) {
  const cfg = AGREEMENT_MAP[status] || AGREEMENT_MAP.none;
  return <span className={`badge ${cfg.cls}`}><span className="dot" />{cfg.label}</span>;
}

export function Badge({ cls = 'muted', children }) {
  return <span className={`badge ${cls}`}>{children}</span>;
}

export function Progress({ value = 0, max = 1, variant = '' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const num = `${Math.round(value)} / ${Math.round(max)} hrs`;
  return (
    <div className="progress-row">
      <div className={`progress${variant === 'green' ? ' green' : variant === 'navy' ? ' navy' : ''}`} style={{ flex: 1 }}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <span className="num">{num}</span>
    </div>
  );
}

export function ProgressBar({ current = 0, total = 1 }) {
  return <Progress value={current} max={total} />;
}

export function KpiCard({ variant = '', icon, label, value, trend, trendDir = 'up', sub }) {
  return (
    <div className={`kpi${variant ? ` ${variant}` : ''}`}>
      {icon && <div className="kpi-icon">{icon}</div>}
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-meta">
        {trend && <span className={`kpi-trend ${trendDir}`}>{trendDir === 'up' ? '↑' : '↓'} {trend}</span>}
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
}

export function Card({ title, sub, action, padded = true, foot, children }) {
  return (
    <div className="card">
      {(title || action) && (
        <div className="card-head">
          <div>
            {title && <h3>{title}</h3>}
            {sub && <div className="card-sub">{sub}</div>}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      {padded ? <div className="card-pad">{children}</div> : children}
      {foot}
    </div>
  );
}

export function Toggle({ on = false, onChange }) {
  return (
    <div
      className={`toggle${on ? ' on' : ''}`}
      onClick={onChange ? () => onChange(!on) : undefined}
    />
  );
}

export function CheckIcon({ size = 13, stroke = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,8 6,12 14,4" />
    </svg>
  );
}

export function XIcon({ size = 13, stroke = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="3" x2="13" y2="13" />
      <line x1="13" y1="3" x2="3" y2="13" />
    </svg>
  );
}

export function SearchIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function LoadingScreen() {
  return <div className="loading-screen">Loading…</div>;
}

export function PageLoad() {
  return (
    <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--ink-3)' }}>
      Loading…
    </div>
  );
}

export function ErrorMsg({ children }) {
  return (
    <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
      {children}
    </div>
  );
}

export function SuccessMsg({ children }) {
  return (
    <div style={{ background: 'var(--status-complete-bg)', color: 'var(--status-complete-fg)', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
      {children}
    </div>
  );
}
